import { AVAILABLE_SCHOOL_IDS, getAvailableSchools } from 'constants/schoolsOfThoughtCatalog';
import { schoolEntryHref } from 'utils/parashariRoutes';

/** Catalog school slugs that can be a learner's primary school. */
export type PrimarySchoolSlug = (typeof AVAILABLE_SCHOOL_IDS)[number];

export const DEFAULT_PRIMARY_SCHOOL_SLUG: PrimarySchoolSlug = 'parashari';

const PRIMARY_SCHOOL_SLUG_SET = new Set<string>(AVAILABLE_SCHOOL_IDS);

/** Legacy Default home values → Parashari school. */
const LEGACY_PREFERRED_HOME_TO_SCHOOL: Record<string, PrimarySchoolSlug> = {
  jyotisha: 'parashari',
  ankotish: 'parashari',
  sadhana: 'parashari',
  bhagyank: 'parashari',
};

export function isPrimarySchoolSlug(value: unknown): value is PrimarySchoolSlug {
  return typeof value === 'string' && PRIMARY_SCHOOL_SLUG_SET.has(value);
}

/**
 * Map a stored preference (new slug or legacy preferred_home_page) to a primary school.
 */
export function coercePrimarySchoolSlug(value: unknown): PrimarySchoolSlug {
  if (isPrimarySchoolSlug(value)) return value;
  if (typeof value === 'string' && LEGACY_PREFERRED_HOME_TO_SCHOOL[value]) {
    return LEGACY_PREFERRED_HOME_TO_SCHOOL[value];
  }
  return DEFAULT_PRIMARY_SCHOOL_SLUG;
}

export function resolvePrimarySchoolSlug(
  profile: { primarySchoolSlug?: string; preferredHomePage?: string } | null | undefined,
): PrimarySchoolSlug {
  if (profile?.primarySchoolSlug) return coercePrimarySchoolSlug(profile.primarySchoolSlug);
  if (profile?.preferredHomePage) return coercePrimarySchoolSlug(profile.preferredHomePage);
  return DEFAULT_PRIMARY_SCHOOL_SLUG;
}

export function learnHrefForPrimarySchool(slug: unknown): string {
  return schoolEntryHref(coercePrimarySchoolSlug(slug));
}

export type PrimarySchoolOption = {
  id: PrimarySchoolSlug;
  name: string;
  description: string;
};

/** Available catalog schools for Settings / onboarding pickers. */
export function getPrimarySchoolOptions(): PrimarySchoolOption[] {
  return getAvailableSchools()
    .filter((school): school is typeof school & { slug: string } => Boolean(school.slug))
    .filter((school) => isPrimarySchoolSlug(school.slug))
    .map((school) => ({
      id: school.slug as PrimarySchoolSlug,
      name: school.name,
      description: school.description,
    }));
}
